import { SystemAlert } from "@cms/components/data/Alert"
import { Button } from "@cms/components/data/Button"
import { ControlledSystemDialog } from "@cms/components/data/Dialog"
import { LabeledSection } from "@cms/components/ui/LabeledSection"
import { useSnackbar } from "@cms/providers/SnackbarProvider"
import api from "@cms/utils/api"
import { generateStoryLink } from "@cms/utils/helpers"
import { useAppDispatch, useAppSelector } from "@cms/utils/store/hooks"
import { setEditorState } from "@cms/utils/store/slices/editor.slice"
import { TEditorStoryVersion } from "@cms/utils/types"
import { slugifyString, WORKFLOWS } from "@dl/declarations"
import { useMutation } from "@tanstack/react-query"
import { useMemo } from "react"

import { FaUndo } from "react-icons/fa"

export const RegenerateURLMenu = () => {
    const dispatch = useAppDispatch()
    const { showSnackbar } = useSnackbar()

    const { story } = useAppSelector((state) => state.editor)

    const mutation = useMutation({
        mutationFn: async () => {
            const response = await api.put<{ slug: string; url: string; previous_urls: string[] }>(`/stories/${story!._id}/url`)
            return response.data
        },
        onSuccess: (data) => {
            dispatch(setEditorState({
                story: {
                    ...story,
                    url: data.url,
                    slug: data.slug,
                    previous_urls: data.previous_urls,
                } as TEditorStoryVersion
            }))

            showSnackbar('The story URL has been successfully regenerated.', 'success')
        },
        onError: (error) => {
            showSnackbar(`Failed to regenerate URL. ${(error as any)?.response?.data?.message || (error as Error).message || 'Please try again later.'}`, 'error')
        },
    })

    const [currentUrl, newUrl] = useMemo(() => {
        if (!story) return ['', '']

        return [
            generateStoryLink(story, true, true, "use_url"),
            generateStoryLink({
                ...story,
                slug: slugifyString(story?.meta_data?.title || story?.headline),
                subtypeObject: {
                    slug: story?.subtypeObject?.slug!
                },
                primaryVerticalObject: {
                    slug: story?.primaryVerticalObject?.slug!
                },
                sub_vertical: story?.sub_vertical
            }, true, true, "use_segments")
        ]
    }, [story])

    const handleRegenerate = () => {
        mutation.mutate()
    }

    const isDisabled = (story?.story_workflow !== WORKFLOWS.Published) || (currentUrl === newUrl)

    return (
        <ControlledSystemDialog
            trigger={{
                component: (
                    <Button
                        tooltip="Please contact the product team before regenerating an URL"
                        inputVariant='gray_text_white_bg'
                        rightIcon={<FaUndo size={18} />}
                        disabled={isDisabled}
                    >
                        Regenerate
                    </Button>
                ),
                disabled: isDisabled,
            }}
            content={{
                component: (
                    <div className="w-full flex flex-col items-start gap-4">
                        <SystemAlert
                            severity="warning"
                        >
                            WARNING! Regenerating a published URL can negatively impact SEO. Please consult the Product team before proceeding with this change.
                        </SystemAlert>

                        <LabeledSection
                            label="Current URL"
                            className="w-full p-2"
                        >
                            <p>
                                {currentUrl}
                            </p>
                        </LabeledSection>

                        <LabeledSection
                            label="New URL"
                            className="w-full p-2 bg-[#237BFF33]"
                        >
                            <p>{newUrl}</p>
                        </LabeledSection>

                        <p
                            className="p-2"
                        >
                            Your story will be published automatically to the new URL below. Are you sure want to continue?
                        </p>
                    </div>
                )
            }}
            dialogProps={{
                title: "Regenerate URL",
                disableScrollLock: false,
                maxWidth: 'sm'
            }}
            actionsProps={{
                submit: {
                    label: 'Regenerate',
                    onClick: handleRegenerate,
                    isLoading: mutation.isPending,
                    disabled: mutation.isPending,
                },
                cancel: {
                    label: 'Cancel'
                }
            }}
        />
    )
}